import { Cushion, MaxWidth, Show, Space } from '@pitchero/react-ui';
import { head, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import RosBottomBanner from '../adverts/ros-bottom-banner';
import TopBannerVideoPlayerSwitch from '../adverts/top-banner-video-player-switch';
import HeaderBackgroundContainer from '../club-frame/header/background-container';
import SecondaryNavContainer from '../navigation/secondary-navigation-container';
import SeoHead from '../seo';
import { recaptchaV3Key } from '../../lib/env';
import ClubRouter from '../../lib/routing';
import ClubLocations from './club-locations';
import ContactLoadingState from './contact-loading-state';
import ContactOfficialPanel from './contact-official-panel';
import OfficialGroup from './official-group';

const ContactPage = ({
  isLoading,
  official,
  officialGroups,
  otherOfficials,
  locations,
  club,
  t,
}) => {
  const [selectedLocation, setSelectedLocation] = useState(head(locations));

  useEffect(() => {
    if (!isEmpty(locations)) {
      setSelectedLocation(locations[0]);
    }
  }, [locations]);

  const onClosePanelHandler = () => {
    ClubRouter.replaceClubRoute(club, 'contact');
  };

  if (isLoading) {
    return <ContactLoadingState />;
  }

  const backgroundImage = () => {
    if (club.sport === 'hockey') {
      // Hockey dugout
      return 'https://images.pitchero.com/ui/1/1447157683_2314.jpg';
    }

    if (club.sport === 'cricket') {
      // Cricket scorebox
      return 'https://images.pitchero.com/ui/1/1447157152_4653.jpg';
    }

    if (club.sport.startsWith('rugby')) {
      // Rugby goals
      return 'https://images.pitchero.com/ui/1/1447162627_5018.jpg';
    }

    // Football pitch
    return 'https://images.pitchero.com/ui/1/1418128090_6942.jpg';
  };

  const hasAtLeastOneUsableAddress = () => {
    if (locations.length > 1) {
      return true;
    }

    if (locations.length === 1) {
      const { address1 } = locations[0];

      return !!address1;
    }

    return false;
  };

  const { advertisingTier, sportId } = club;

  return (
    <>
      <SeoHead
        club={club}
        descriptionTranslationKey="common:contact_meta_description"
        route="contact"
        titleTranslationKey="common:contact_page_title"
      />
      <GoogleReCaptchaProvider reCaptchaKey={recaptchaV3Key()}>
        <ContactOfficialPanel
          isOpen={!!official}
          onClose={onClosePanelHandler}
          official={official}
          club={club}
        />
      </GoogleReCaptchaProvider>
      <Show maxWidth="fullHeader" component="div">
        <SecondaryNavContainer title="Contact" />
      </Show>
      <Cushion top="medium" component="div">
        <TopBannerVideoPlayerSwitch
          advertisingTier={advertisingTier}
          pageType="contact"
          sportId={sportId}
        />
      </Cushion>

      <HeaderBackgroundContainer
        imgSrc={backgroundImage()}
        preset="headline"
        height={hasAtLeastOneUsableAddress() ? 200 : 0}
      >
        {hasAtLeastOneUsableAddress() && (
          <MaxWidth component="div" maxWidth={1366} center>
            <Cushion
              component="div"
              horizontal="small"
              vertical="medium"
              responsive={[{ minWidth: 'tab', props: { horizontal: 'large' } }]}
            >
              <ClubLocations
                selectedLocation={selectedLocation}
                locations={locations}
                setSelectedLocation={setSelectedLocation}
                club={club}
              />
            </Cushion>
          </MaxWidth>
        )}
      </HeaderBackgroundContainer>

      <Cushion
        component="div"
        all="small"
        responsive={[{ minWidth: 'tab', props: { all: 'large' } }]}
        className="contact-page"
      >
        {officialGroups && (
          <MaxWidth component="div" maxWidth={1366} center>
            {officialGroups.map(
              (group) =>
                group.officials.length > 0 && (
                  <Space key={`${group.name}`} bottom="xlarge" component="div">
                    <OfficialGroup officialGroup={group} />
                  </Space>
                ),
            )}
            <Cushion vertical="medium">
              <RosBottomBanner />
            </Cushion>
            {!isEmpty(otherOfficials) && (
              <OfficialGroup
                officialGroup={{
                  officials: otherOfficials,
                  name: t('common:other_officials_section_title'),
                }}
              />
            )}
          </MaxWidth>
        )}
      </Cushion>
    </>
  );
};

ContactPage.defaultProps = {
  official: null,
};

ContactPage.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  club: PropTypes.PropTypes.shape().isRequired,
  officialGroups: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  otherOfficials: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  locations: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  official: PropTypes.shape(),
  t: PropTypes.func.isRequired,
};

export default ContactPage;
